import React from "react";
import { Clock, Sparkles } from "lucide-react";
import FeatureStatusBadge, { FeatureStatus } from "./FeatureStatusBadge";
import ModalShell from "../../../components/ui/ModalShell";
import Button from "../../../components/ui/Button";
import { useLanguage } from "../../../components/ui/LanguageProvider";

type ComingSoonModalProps = {
  open: boolean;
  onClose: () => void;
  featureName?: string;
  featureStatus?: FeatureStatus;
  plannedFeatures?: string[];
};

export default function ComingSoonModal({
  open,
  onClose,
  featureName,
  featureStatus = "coming_soon",
  plannedFeatures,
}: ComingSoonModalProps) {
  const { t, language } = useLanguage();
  const features = plannedFeatures ?? (language === "en"
    ? ["Status sync", "Send history", "External service integration", "Automatic notifications"]
    : [
    "상태 동기화",
    "발송 이력 저장",
    "외부 서비스 연동",
    "자동 알림",
  ]);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      size="sm"
      dismissOnBackdrop
      icon={
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "var(--radius-md)",
            background: "var(--accent-soft)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Clock size={18} color="var(--accent)" />
        </div>
      }
      title={t("현재 개발 중인 기능입니다.", "This feature is under development.")}
      subtitle={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {featureName ?? t("이 기능", "This feature")}
          <FeatureStatusBadge status={featureStatus} size="sm" />
        </span>
      }
      footer={<Button onClick={onClose}>{t("확인", "OK")}</Button>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", padding: "var(--space-5)" }}>
        <div style={bodyBoxStyle}>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            {t("이 기능은 향후 연동 예정입니다. 현재는 UI Draft 상태이며, 실제 발송/연동/자동화는 추후 개발됩니다.", "This feature will be integrated later. It is currently a UI draft; actual sending/integration/automation will be developed later.")}
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <Sparkles size={12} />
            {t("예정 기능", "Planned features")}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {features.map((feature) => (
              <div key={feature} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--accent)", flexShrink: 0 }} />
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

const bodyBoxStyle: React.CSSProperties = {
  background: "var(--bg)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-md)",
  padding: 14,
};

const sectionTitleStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 10,
  fontSize: 12,
  fontWeight: 700,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
